import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { ConfigService, Globals } from '@anduin/config';
import {
  APP_VERSION_TOKEN,
  AssignedDraftBillsUpdated,
  GetFirmHasJobTemplates,
  Loadable,
  LoadableHelper,
  LoadableStatus,
  SetAppVersion,
} from '@anduin/shared';
import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AppStateModel, DEFAULT_APP_STATE_MODEL } from './app.model';

@Injectable({
  providedIn: 'root',
})
@State({
  name: 'app',
  defaults: DEFAULT_APP_STATE_MODEL,
})
export class AppState implements NgxsOnInit {
  constructor(
    private readonly http: HttpClient,
    private readonly globals: Globals,
    private readonly configs: ConfigService,
    @Optional() @Inject(APP_VERSION_TOKEN) private readonly appVersion = 'dev',
  ) {}

  ngxsOnInit({ dispatch }: StateContext<any>): void {
    dispatch(new SetAppVersion(this.appVersion));
  }

  /**
   * Get the number of draft bills that are assigned to the user.
   * @param assignedDraftBillCount
   */
  @Selector()
  static getAssignedDraftBillCount({ assignedDraftBillCount }: AppStateModel): number {
    return assignedDraftBillCount;
  }

  @Selector()
  static getFirmHasJobTemplatesLoadable({ firmHasJobTemplates }: AppStateModel): Loadable<boolean> {
    return firmHasJobTemplates;
  }

  @Selector([AppState.getFirmHasJobTemplatesLoadable])
  static getFirmHasJobTemplatesStatus({ status }: Loadable<boolean>): LoadableStatus {
    return status;
  }

  @Selector([AppState.getFirmHasJobTemplatesLoadable])
  static getFirmHasJobTemplates({ data }: Loadable<boolean>): boolean {
    return data;
  }

  @Action([AssignedDraftBillsUpdated])
  assignedDraftBillsUpdated(
    { patchState }: StateContext<AppStateModel>,
    { newCount }: AssignedDraftBillsUpdated,
  ): Observable<number> | void {
    if (!this.configs.billingEnabled) {
      return;
    }

    // If no override count is given, pull the count from the API
    if (newCount == null) {
      return this.http
        .get<number>(`${this.globals.actionEngineUrl}/api/secure/v1/bill/assigned/count`)
        .pipe(tap((count) => patchState({ assignedDraftBillCount: count })));
    }

    // Otherwise, set the override count
    patchState({
      assignedDraftBillCount: newCount,
    });
  }

  @Action([GetFirmHasJobTemplates])
  getFirmHasJobTemplates(
    { patchState, getState }: StateContext<AppStateModel>,
    { getFirmHasJobsTemplatesObservable }: GetFirmHasJobTemplates,
  ): Observable<boolean> {
    if (!LoadableHelper.shouldRequest(getState().firmHasJobTemplates.status)) {
      return;
    }

    return getFirmHasJobsTemplatesObservable.pipe(
      tap(
        (firmHasJobTemplates) => patchState({ firmHasJobTemplates: { status: LoadableStatus.LOADED, data: firmHasJobTemplates } }),
        (_) => patchState({ firmHasJobTemplates: { status: LoadableStatus.ERROR } }),
      ),
    );
  }
}
